import { computeSkuRisk } from '../../operational-intelligence';
import { computeIntegrationHealth } from '../../operational-intelligence/integrations/integrationHealth';

export const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2 };
export const OVERDUE_PO_ALERT_TYPE = 'overdue_purchase_order';
export const PROCUREMENT_WORKFLOW_KEY = 'procurement_workflow_state';
const OVERDUE_PO_ACTIVE_STATUSES = new Set(['approved', 'ordered', 'confirmed', 'shipped']);

export function buildOverduePurchaseOrderActions({
  poList = [],
  organizationId = null,
  now = new Date(),
}) {
  return poList
    .filter(po => {
      const expectedDelivery = po.expectedDelivery || po.expectedDeliveryDate;
      return (
        expectedDelivery &&
        new Date(expectedDelivery) < now &&
        OVERDUE_PO_ACTIVE_STATUSES.has(po.status)
      );
    })
    .slice(0, 3)
    .map(po => {
      const expectedDelivery = po.expectedDelivery || po.expectedDeliveryDate;
      const daysLate = Math.floor((now - new Date(expectedDelivery)) / 86400000);
      const supplierName = po.supplier?.name;

      return {
        priority: 'critical',
        alertId: `overdue-po:${po._id || po.poNumber || `unknown-${daysLate}`}`,
        alertType: OVERDUE_PO_ALERT_TYPE,
        severity: 'critical',
        createdAt: now.toISOString(),
        organizationId,
        action: supplierName
          ? `Contact ${supplierName} - ${po.poNumber || 'overdue PO'}`
          : `Follow up on ${po.poNumber || 'overdue PO'}`,
        context: `${daysLate} day${daysLate !== 1 ? 's' : ''} past expected delivery`,
        impact: 'Unblock procurement',
        href: '/purchase-orders',
      };
    });
}

export function buildProcurementDraftState(item, now = new Date()) {
  const shortageToReorderPoint = Math.max(
    0,
    Number(item.reorderPointUnits || 0) - Number(item.availableUnits || 0)
  );
  const quantityToOrder = Math.max(
    1,
    shortageToReorderPoint || Number(item.reorderPointUnits || 0) || 1
  );
  const leadTimeDays = Number(item.leadTimeDays || 0);
  const expectedDeliveryDate = Number.isFinite(leadTimeDays) && leadTimeDays > 0
    ? new Date(now.getTime() + (leadTimeDays * 24 * 60 * 60 * 1000)).toISOString().slice(0, 10)
    : '';

  return {
    recommendationId: item.recommendationId || item.productId || item.sku || null,
    productId: item.productId || null,
    productName: item.name || item.sku || 'Unknown product',
    sku: item.sku || 'Unknown SKU',
    quantityToOrder,
    unitPrice: Number(item.unitPrice || item.price || 0),
    supplierId: item.supplierId || null,
    supplierName: item.supplierName || null,
    reason: item.reason || item.context || 'Operational recommendation',
    confidence: item.confidence || 'unknown',
    daysOfCover: item.daysOfCover ?? null,
    leadTimeDays: item.leadTimeDays ?? null,
    expectedDeliveryDate,
    source: 'recommendation',
    workflowStatus: 'draft_pending',
  };
}

export function readProcurementWorkflowState(readStorage = () => localStorage.getItem(PROCUREMENT_WORKFLOW_KEY)) {
  try {
    const raw = readStorage();
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeProcurementWorkflowState(state, writeStorage = (value) => localStorage.setItem(PROCUREMENT_WORKFLOW_KEY, value)) {
  try {
    writeStorage(JSON.stringify(state));
  } catch {
    // Ignore storage failures; the workflow remains usable without persistence.
  }
}

export function clearProcurementWorkflowState(removeStorage = () => localStorage.removeItem(PROCUREMENT_WORKFLOW_KEY)) {
  try {
    removeStorage();
  } catch {
    // Ignore storage failures.
  }
}

function applyProcurementWorkflowState(queue, workflowState, poList = []) {
  const poByRecommendation = new Map(
    poList
      .filter(po => po?.sourceRecommendationId)
      .map(po => [String(po.sourceRecommendationId), po])
  );

  if (!workflowState && poByRecommendation.size === 0) return queue;

  const workflowKeys = new Set([
    workflowState?.productId ? String(workflowState.productId) : null,
    workflowState?.sku ? String(workflowState.sku) : null,
    workflowState?.recommendationId ? String(workflowState.recommendationId) : null,
  ].filter(Boolean));

  const matchedPurchaseOrder = workflowState?.purchaseOrderId
    ? poList.find(po => String(po._id || '') === String(workflowState.purchaseOrderId))
    : null;

  return queue.map(item => {
    if (item.recommendationType !== 'procurement') {
      return item;
    }

    const itemKeys = [item.productId, item.sku].filter(Boolean).map(value => String(value));
    const linkedByRecommendation = itemKeys
      .map(key => poByRecommendation.get(key))
      .find(Boolean) || null;
    const matchesWorkflow = workflowState
      ? itemKeys.some(key => workflowKeys.has(key))
      : false;

    if (!linkedByRecommendation && !matchesWorkflow) {
      return item;
    }

    const linkedPo = linkedByRecommendation || matchedPurchaseOrder;
    const currentPoStatus = linkedPo?.status || workflowState?.purchaseOrderStatus || null;
    const currentPoNumber = linkedPo?.orderNumber || workflowState?.purchaseOrderNumber || null;
    const currentPoId = linkedPo?._id || workflowState?.purchaseOrderId || null;

    if (linkedPo) {
      return {
        ...item,
        action: 'Draft Created',
        context: currentPoNumber
          ? `PO ${currentPoNumber} • ${currentPoStatus || 'draft'} • ${item.context}`
          : `PO ${currentPoStatus || 'draft'} • ${item.context}`,
        impact: 'Track purchase order status',
        href: currentPoId ? `/purchase-orders/${currentPoId}` : '/purchase-orders',
        workflowStatus: 'draft_created',
        purchaseOrderId: currentPoId,
        purchaseOrderNumber: currentPoNumber,
        purchaseOrderStatus: currentPoStatus,
      };
    }

    if (workflowState?.workflowStatus === 'draft_created') {
      return {
        ...item,
        action: 'Draft Created',
        context: currentPoNumber
          ? `PO ${currentPoNumber} • ${currentPoStatus || 'draft'} • ${item.context}`
          : `PO ${currentPoStatus || 'draft'} • ${item.context}`,
        impact: 'Track purchase order status',
        href: currentPoId
          ? `/purchase-orders/${currentPoId}`
          : '/purchase-orders',
        workflowStatus: 'draft_created',
        purchaseOrderId: currentPoId,
        purchaseOrderNumber: currentPoNumber,
        purchaseOrderStatus: currentPoStatus,
      };
    }

    if (workflowState?.workflowStatus === 'draft_pending') {
      return {
        ...item,
        action: 'Create Draft PO',
        context: `Ready to draft • ${item.context}`,
        impact: 'Start procurement workflow',
        href: '/purchase-orders/new',
        workflowStatus: 'draft_pending',
      };
    }

    return item;
  });
}

export function buildExecutionQueue({
  forecastSignals = [],
  lowStockProducts,
  poList,
  pendingOrders,
  receiptsCount,
  fbaLabelsCount,
  integrationCredentials,
  readOrgId,
  procurementWorkflow,
}) {
  const queue = [];
  const today = new Date();
  const organizationId = readOrgId ? readOrgId() : null;
  const prioritizedSkuKeys = new Set();

  const activeCredential =
    integrationCredentials.find(c => c.status !== 'disconnected') ??
    integrationCredentials[0] ??
    null;
  const integrationSignal = activeCredential
    ? computeIntegrationHealth(activeCredential)
    : null;

  if (
    integrationSignal?.level === 'auth-error' ||
    integrationSignal?.level === 'disconnected'
  ) {
    queue.push({
      priority: 'critical',
      recommendationType: 'amazon-integration-recovery',
      action: 'Reconnect Amazon',
      context: integrationSignal.label,
      impact: 'Restore data sync',
      href: '/integrations/amazon?reconnect=1&source=command-center',
    });
  }

  const recommendedNow = forecastSignals
    .filter(signal => signal?.needsAction)
    .sort((a, b) => {
      const rank = { critical: 0, high: 1, medium: 2, low: 3 };
      return (rank[a.riskLevel] ?? 9) - (rank[b.riskLevel] ?? 9);
    });

  for (const signal of recommendedNow.slice(0, 6)) {
    const skuKey = String(signal.productId || signal.sku || '');
    if (!skuKey) continue;
    prioritizedSkuKeys.add(skuKey);

    const shortageToReorderPoint = Math.max(
      0,
      Number(signal.reorderPointUnits || 0) - Number(signal.availableUnits || 0)
    );

    const coverContext = signal.daysOfCover === null
      ? signal.reason
      : `${signal.daysOfCover}d cover`;
    const confidence = signal.forecastConfidence || 'unknown';

    queue.push({
      priority: signal.riskLevel === 'critical' ? 'critical' : 'high',
      recommendationType: 'procurement',
      sku: signal.sku,
      name: signal.name,
      reason: coverContext,
      confidence,
      action: `Create Draft PO`,
      context: `${signal.name || signal.sku} • ${coverContext} • ${confidence} confidence`,
      impact: shortageToReorderPoint > 0
        ? `Recover ${shortageToReorderPoint} units to reorder target`
        : 'Protect availability',
      href: '/purchase-orders/new',
      productId: signal.productId || null,
      availableUnits: Number(signal.availableUnits || 0),
      reorderPointUnits: Number(signal.reorderPointUnits || 0),
      leadTimeDays: Number(signal.leadTimeDays || 0),
      daysOfCover: signal.daysOfCover ?? null,
      supplierId: signal.supplierId || null,
      supplierName: signal.supplierName || null,
    });
  }

  const atRiskSkus = lowStockProducts
    .map(p => ({ product: p, risk: computeSkuRisk(p) }))
    .filter(({ product, risk }) => {
      const skuKey = String(product._id || product.sku || '');
      return !prioritizedSkuKeys.has(skuKey) && (risk.level === 'stock-out' || risk.level === 'critical');
    })
    .slice(0, 5);

  for (const { product, risk } of atRiskSkus) {
    queue.push({
      priority: 'critical',
      action: `Reorder ${product.name || product.sku}`,
      context:
        risk.level === 'stock-out'
          ? 'No units remaining - revenue exposure'
          : risk.sub ?? `${product.quantity ?? product.stock ?? 0} units`,
      impact: 'Prevent revenue loss',
      href: '/inventory/smart-reorder',
    });
  }

  queue.push(...buildOverduePurchaseOrderActions({ poList, organizationId, now: today }));

  const pendingApprovalPOs = poList.filter(po =>
    ['draft', 'pending'].includes(po.status)
  );
  if (pendingApprovalPOs.length > 0) {
    queue.push({
      priority: 'high',
      action: `Approve ${pendingApprovalPOs.length} purchase order${pendingApprovalPOs.length !== 1 ? 's' : ''}`,
      context: 'Awaiting confirmation before sending to supplier',
      impact: 'Enable ordering',
      href: '/purchase-orders?status=draft,pending',
    });
  }

  const lowSkus = lowStockProducts
    .map(p => ({ product: p, risk: computeSkuRisk(p) }))
    .filter(({ product, risk }) => {
      const skuKey = String(product._id || product.sku || '');
      return !prioritizedSkuKeys.has(skuKey) && risk.level === 'low';
    })
    .slice(0, 3);

  for (const { product, risk } of lowSkus) {
    queue.push({
      priority: 'high',
      action: `Plan reorder for ${product.name || product.sku}`,
      context: risk.sub ?? `${product.quantity ?? product.stock ?? 0} units`,
      impact: 'Prevent future stockout',
      href: '/inventory/smart-reorder',
    });
  }

  if (receiptsCount > 0) {
    queue.push({
      priority: 'high',
      action: `Receive ${receiptsCount} pending transfer${receiptsCount !== 1 ? 's' : ''}`,
      context: 'Inbound stock not yet applied to inventory',
      impact: 'Update stock levels',
      href: '/warehouse-transfers?status=pending',
    });
  }

  if (fbaLabelsCount > 0) {
    queue.push({
      priority: 'high',
      action: `Generate labels for ${fbaLabelsCount} FBA shipment${fbaLabelsCount !== 1 ? 's' : ''}`,
      context: 'Cannot ship to FBA without FNSKU labels',
      impact: 'Unblock shipment',
      href: '/fba-shipments?status=pending-labels',
    });
  }

  if (pendingOrders > 0) {
    queue.push({
      priority: 'medium',
      action: `Process ${pendingOrders} pending order${pendingOrders !== 1 ? 's' : ''}`,
      context: 'Orders awaiting fulfillment',
      impact: 'Fulfill demand',
      href: '/orders?status=pending',
    });
  }

  if (
    integrationSignal?.level === 'sync-error' ||
    integrationSignal?.level === 'stale'
  ) {
    queue.push({
      priority: 'medium',
      action: 'Review Amazon sync status',
      context: integrationSignal.label,
      impact: 'Restore inventory accuracy',
      href: '/integrations/amazon',
    });
  }

  const workflowAwareQueue = applyProcurementWorkflowState(queue, procurementWorkflow, poList);

  return workflowAwareQueue.sort(
    (a, b) =>
      (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99)
  );
}

export function reconcileOverdueAlertLifecycle({
  doNow,
  shownAlerts,
  seenAlerts,
  track,
  now,
  readOrgId,
}) {
  const overdueActions = doNow.filter(
    item => item.alertType === OVERDUE_PO_ALERT_TYPE && item.alertId
  );

  const activeIds = new Set(overdueActions.map(item => item.alertId));

  overdueActions.forEach(item => {
    if (shownAlerts.has(item.alertId)) {
      return;
    }

    shownAlerts.add(item.alertId);
    seenAlerts.set(item.alertId, {
      firstShownAt: now.toISOString(),
      createdAt: item.createdAt,
      organizationId: item.organizationId || readOrgId(),
    });

    track('alert_shown', {
      alertId: item.alertId,
      alertType: item.alertType,
      organizationId: item.organizationId || readOrgId(),
      severity: item.severity || item.priority || 'critical',
      createdAt: item.createdAt,
    });
  });

  Array.from(seenAlerts.entries()).forEach(([alertId, state]) => {
    if (activeIds.has(alertId)) {
      return;
    }

    const nowIso = now.toISOString();
    const firstShownAt = state.firstShownAt || nowIso;
    const lifetimeHours = Math.max(
      0,
      Number(((now.getTime() - new Date(firstShownAt).getTime()) / 3600000).toFixed(2))
    );

    track('resolution_detected', {
      alertId,
      alertType: OVERDUE_PO_ALERT_TYPE,
      resolutionMethod: 'alert_removed_from_queue',
      organizationId: state.organizationId || readOrgId(),
      resolvedAt: nowIso,
    });

    track('alert_cleared', {
      alertId,
      alertType: OVERDUE_PO_ALERT_TYPE,
      lifetimeHours,
      organizationId: state.organizationId || readOrgId(),
      clearedAt: nowIso,
    });

    seenAlerts.delete(alertId);
    shownAlerts.delete(alertId);
  });
}

export function emitOverdueActionClicked(item, track, readOrgId, now) {
  if (!(item.alertType === OVERDUE_PO_ALERT_TYPE && item.alertId)) {
    return;
  }

  track('action_clicked', {
    alertId: item.alertId,
    alertType: item.alertType,
    actionType: 'navigate_to_resolution_route',
    destinationRoute: item.href,
    organizationId: item.organizationId || readOrgId(),
    clickedAt: now.toISOString(),
  });
}
